import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, Trash2, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
export function CartModal() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    cartTotal,
    isCartOpen,
    setIsCartOpen
  } = useCart();
  const freeShippingThreshold = 500;
  const progress = Math.min(cartTotal / freeShippingThreshold * 100, 100);
  const remainingForFreeShipping = Math.max(freeShippingThreshold - cartTotal, 0);
  return <AnimatePresence data-id="element-25">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 0.5
      }} exit={{
        opacity: 0
      }} onClick={() => setIsCartOpen(false)} className="fixed inset-0 bg-black z-40" data-id="element-26" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-27">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between" data-id="element-28">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-29">
                <ShoppingBag className="w-5 h-5" data-id="element-30" />
                Carrito ({items.length})
              </h2>
              <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-31">
                <X className="w-5 h-5 text-gray-500" data-id="element-32" />
              </button>
            </div>

            {/* Free Shipping Bar */}
            {items.length > 0 && <div className="px-4 py-3 bg-gray-50 border-b" data-id="element-33">
                <div className="flex items-center gap-2 text-sm mb-2" data-id="element-34">
                  <Truck className="w-4 h-4 text-[#0056b3]" data-id="element-35" />
                  {remainingForFreeShipping > 0 ? <span className="text-gray-600" data-id="element-36">
                      Agrega{' '}
                      <span className="font-semibold text-[#0056b3]" data-id="element-37">
                        ${remainingForFreeShipping.toFixed(2)}
                      </span>{' '}
                      más para envío gratis
                    </span> : <span className="text-green-600 font-medium" data-id="element-38">
                      ¡Tienes envío gratis!
                    </span>}
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-39">
                  <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} className={`h-full ${progress >= 100 ? 'bg-green-500' : 'bg-[#0056b3]'}`} data-id="element-40" />
                </div>
              </div>}

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto" data-id="element-41">
              {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8" data-id="element-42">
                  <ShoppingBag className="w-16 h-16 mb-4 opacity-30" data-id="element-43" />
                  <p className="text-lg font-medium text-gray-500 mb-2" data-id="element-44">
                    Tu carrito está vacío
                  </p>
                  <p className="text-sm text-gray-400 mb-4 text-center" data-id="element-45">
                    Agrega productos para comenzar tu compra
                  </p>
                  <button onClick={() => setIsCartOpen(false)} className="text-[#0056b3] font-medium hover:underline" data-id="element-46">
                    Continuar comprando
                  </button>
                </div> : <div className="p-4 space-y-4" data-id="element-47">
                  {items.map(item => <motion.div key={item.id} layout initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} className="flex gap-3 pb-4 border-b border-gray-100" data-id="element-48">
                      <div className="w-20 h-20 bg-gray-50 rounded-md overflow-hidden flex-shrink-0" data-id="element-49">
                        <img src={item.imageUrl} alt={item.title} className="w-full h-full object-contain p-2" data-id="element-50" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-51">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1" data-id="element-52">
                          {item.title}
                        </h3>
                        <p className="text-xs text-gray-400 mb-2" data-id="element-53">
                          {item.brand}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-54">
                          <div className="flex items-center border border-gray-200 rounded" data-id="element-55">
                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-1.5 hover:bg-gray-50 text-gray-500" data-id="element-56">
                              <Minus className="w-3 h-3" data-id="element-57" />
                            </button>
                            <span className="px-3 text-sm font-medium" data-id="element-58">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1.5 hover:bg-gray-50 text-gray-500" data-id="element-59">
                              <Plus className="w-3 h-3" data-id="element-60" />
                            </button>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-61">
                            <span className="font-semibold text-gray-900" data-id="element-62">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                            <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500 p-1" data-id="element-63">
                              <Trash2 className="w-4 h-4" data-id="element-64" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>)}
                </div>}
            </div>

            {/* Footer */}
            {items.length > 0 && <div className="p-4 border-t bg-white" data-id="element-65">
                <div className="flex justify-between items-center mb-4" data-id="element-66">
                  <span className="text-gray-600" data-id="element-67">Subtotal</span>
                  <span className="text-xl font-bold text-gray-900" data-id="element-68">
                    ${cartTotal.toFixed(2)}
                  </span>
                </div>
                <div className="space-y-2" data-id="element-69">
                  <Link to="/checkout" onClick={() => setIsCartOpen(false)} className="block w-full py-3 bg-[#0056b3] text-white rounded-md font-medium hover:bg-[#004494] transition-colors text-center" data-id="element-70">
                    Finalizar Compra
                  </Link>
                  <button onClick={() => setIsCartOpen(false)} className="block w-full py-3 border border-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition-colors" data-id="element-71">
                    Seguir Comprando
                  </button>
                </div>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}